/**
 * SchoolRun Driver Service
 * Manages driver profiles, documents, vehicles, journeys, routes, earnings, and operations.
 * All data comes from the authenticated Laravel user — no mock fallbacks.
 */

import { apiClient, USE_MOCK_API } from '@/lib/apiClient';
import {
  Driver,
  DriverDocument,
  DriverVerification,
  Vehicle,
  Passenger,
  Incident,
  IncidentCreatePayload,
  Route,
  ApiCollectionResponse,
  ApiSingleResponse,
} from '@/types/domain';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export type { IncidentCreatePayload };

export interface DriverProfileUpdatePayload {
  f_name?: string;
  l_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  bio?: string;
  city?: string;
  city_id?: number | string | null;
  suburb?: string;
  address?: string;
  years_experience?: string;
}

export interface DriverServiceArea {
  city: { id: number; name: string } | null;
  city_id: number | null;
  suburbs: { id: number; name: string }[];
  schools: { id: number; name: string }[];
}

export interface DriverServiceAreaUpdatePayload {
  city_id?: number | string | null;
  suburb_ids?: (number | string)[];
  school_ids?: (number | string)[];
}

/**
 * Raw driver profile payload returned by GET/PUT /api/driver/profile.
 * This is the authenticated user's own record — not the public Driver
 * domain model used for parent-facing route cards.
 */
export interface DriverProfileRecord {
  id: number;
  f_name: string;
  l_name: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  image: string | null;
  image_url?: string | null;
  address: string | null;
  city: { id: number; name: string } | null;
  city_id: number | null;
  years_experience?: string | null;
  bio?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface VehiclePayload {
  license_plate: string;
  vehicle_type?: string;
  make?: string;
  model?: string;
  year?: number;
  capacity: number;
  color?: string;
}

// Laravel vehicle → frontend Vehicle domain shape
const mapVehicle = (v: any): Vehicle => ({
  id: String(v.id),
  make: v.make || v.vehicle_type || '',
  model: v.model || '',
  year: v.year,
  capacity: v.capacity,
  reg: v.license_plate || '',
  color: v.color || '',
  status: v.is_active ? 'active' : 'inactive',
});

export interface WithdrawalRequestPayload {
  amount: number;
  withdrawal_method_id?: number;
  notes?: string;
}

export interface WithdrawalRecord {
  id: string;
  amount: number;
  currency?: string;
  method?: string;
  details?: string;
  status: string; // requested|processing|completed|failed|rejected|reversed
  reference?: string;
  external_reference?: string;
  created_at: string;
  completed_at?: string;
  failed_at?: string;
  failure_reason?: string;
  rejection_reason?: string;
  notes?: string;
  scheduled?: boolean;
}

export interface DriverBalances {
  pending: string;
  available: string;
  reserved: string;
  paid_to_date: string;
  wallet_balance: string;
  currency: string;
  minimum_payout: string;
  settlement_delay_hours: number;
  methods: string[];
  next_payout: {
    frequency: string;
    date: string;
    eligible: boolean;
    estimated_amount: string;
  } | null;
}

export interface AttendanceRecord {
  id: string;
  child_name: string;
  pickup_time?: string;
  dropoff_time?: string;
  status: string;
  journey_date: string;
  route_name?: string;
}

export interface PreRouteCheckVehicle {
  id: number;
  license_plate?: string;
  vehicle_type?: string;
  model?: string;
  year?: number;
  capacity?: number;
  color?: string;
  is_active?: boolean;
}

export interface PreRouteCheck {
  can_start: boolean;
  blocking_reasons: string[];
  vehicle_valid: boolean;
  vehicle: PreRouteCheckVehicle | null;
  route_assigned: boolean;
  route_has_vehicle: boolean;
  route_has_passengers: boolean;
  documents_valid: boolean;
  documents: DriverDocument[];
  driver_verified: boolean;
}

export interface VehicleOptionRecord {
  id: number;
  kind: 'type' | 'make' | 'model' | 'color';
  name: string;
  parent_id: number | null;
}

export interface DocumentRequirement {
  type: string;
  label: string;
  description?: string | null;
  required: boolean;
  active: boolean;
  display_order: number;
  uploaded?: boolean;
  status?: string;
  document?: DriverDocument | null;
}

export interface OnboardingSummary {
  onboarding_complete: boolean;
  required_documents: {
    type: string;
    label: string;
    required: boolean;
    uploaded: boolean;
    status: string;
  }[];
  missing_required_documents: string[];
}

export const DriverService = {
  // ─── Profile ──────────────────────────────────────────────────────────────

  /**
   * The authenticated driver's own profile record (raw Laravel user fields).
   */
  async getMyProfile(): Promise<DriverProfileRecord> {
    const response =
      await apiClient.get<ApiSingleResponse<DriverProfileRecord>>('/api/driver/profile');
    return response.data;
  },

  async getProfile(): Promise<Driver> {
    if (USE_MOCK_API) {
      await delay(300);
      throw new Error('Driver profile not available in mock mode. Connect to Laravel API.');
    }
    const response = await apiClient.get<ApiSingleResponse<Driver>>('/api/driver/profile');
    return response.data;
  },

  async updateProfile(payload: DriverProfileUpdatePayload): Promise<Driver> {
    if (USE_MOCK_API) {
      await delay(600);
      throw new Error('Profile update not available in mock mode.');
    }
    const response = await apiClient.put<ApiSingleResponse<Driver>>('/api/driver/profile', payload);
    return response.data;
  },

  // ─── Service Area ─────────────────────────────────────────────────────────

  async getServiceArea(): Promise<DriverServiceArea> {
    const response = await apiClient.get<ApiSingleResponse<DriverServiceArea>>(
      '/api/driver/service-area'
    );
    return response.data;
  },

  async updateServiceArea(payload: DriverServiceAreaUpdatePayload): Promise<DriverServiceArea> {
    const response = await apiClient.put<ApiSingleResponse<DriverServiceArea>>(
      '/api/driver/service-area',
      payload
    );
    return response.data;
  },

  // ─── Documents ────────────────────────────────────────────────────────────

  async getDocuments(): Promise<DriverDocument[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response =
      await apiClient.get<ApiCollectionResponse<DriverDocument>>('/api/driver/documents');
    return response.data;
  },

  async getDocumentRequirements(): Promise<DocumentRequirement[]> {
    const response = await apiClient.get<{ data: DocumentRequirement[] }>(
      '/api/driver/document-requirements'
    );
    return response.data;
  },

  async getOnboardingSummary(): Promise<OnboardingSummary> {
    const response = await apiClient.get<OnboardingSummary>('/api/driver/onboarding');
    return response;
  },

  async uploadDocument(
    type: string,
    file: File,
    metadata?: { expiry_date?: string; issue_date?: string }
  ): Promise<DriverDocument> {
    if (USE_MOCK_API) {
      await delay(1500);
      throw new Error('Document upload not available in mock mode.');
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (metadata?.expiry_date) formData.append('expiry_date', metadata.expiry_date);
    if (metadata?.issue_date) formData.append('issue_date', metadata.issue_date);
    const response = await apiClient.upload<ApiSingleResponse<DriverDocument>>(
      '/api/driver/documents',
      formData
    );
    return response.data;
  },

  async replaceDocument(docId: string, file: File): Promise<DriverDocument> {
    if (USE_MOCK_API) {
      await delay(1200);
      throw new Error('Document replacement not available in mock mode.');
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.upload<ApiSingleResponse<DriverDocument>>(
      `/api/driver/documents/${docId}/replace`,
      formData
    );
    return response.data;
  },

  /**
   * Upload/replace the driver's profile photo.
   * POST /api/me/photo — Laravel validates and replaces the previous image;
   * parents see it via driver serialization (profile_photo_url).
   */
  async uploadProfilePhoto(file: File): Promise<{ image: string; image_url: string }> {
    if (USE_MOCK_API) {
      await delay(1000);
      throw new Error('Profile photo upload not available in mock mode.');
    }
    const formData = new FormData();
    formData.append('photo', file);
    const response = await apiClient.upload<
      ApiSingleResponse<{ image: string; image_url: string }>
    >('/api/me/photo', formData);
    return response.data;
  },

  // ─── Verification ─────────────────────────────────────────────────────────

  async getVerification(): Promise<DriverVerification> {
    if (USE_MOCK_API) {
      await delay(400);
      throw new Error('Verification status not available in mock mode. Connect to Laravel API.');
    }
    const response = await apiClient.get<ApiSingleResponse<DriverVerification>>(
      '/api/driver/verification'
    );
    return response.data;
  },

  async getPreRouteCheck(): Promise<PreRouteCheck> {
    if (USE_MOCK_API) {
      await delay(300);
      throw new Error('Pre-route check not available in mock mode. Connect to Laravel API.');
    }
    const response = await apiClient.get<ApiSingleResponse<PreRouteCheck>>(
      '/api/driver/pre-route-check'
    );
    return response.data;
  },

  // ─── Vehicles ─────────────────────────────────────────────────────────────

  async getVehicleOptions(
    kind: VehicleOptionRecord['kind'],
    search = '',
    parentId?: number | null
  ): Promise<VehicleOptionRecord[]> {
    const params = new URLSearchParams({ kind });
    if (search) params.set('search', search);
    if (parentId) params.set('parent_id', String(parentId));
    const response = await apiClient.get<{ data: VehicleOptionRecord[] }>(
      `/api/vehicle-options?${params.toString()}`
    );
    return response.data;
  },

  async createVehicleOption(
    kind: VehicleOptionRecord['kind'],
    name: string,
    parentId?: number | null
  ): Promise<VehicleOptionRecord> {
    const response = await apiClient.post<{ data: VehicleOptionRecord }>('/api/vehicle-options', {
      kind,
      name,
      parent_id: parentId ?? undefined,
    });
    return response.data;
  },

  async getVehicles(): Promise<Vehicle[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<any>('/api/vehicles');
    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : (response?.data?.data ?? []);
    return list.map(mapVehicle);
  },

  async createVehicle(payload: VehiclePayload): Promise<Vehicle> {
    if (USE_MOCK_API) {
      await delay(800);
      throw new Error('Vehicle creation not available in mock mode.');
    }
    const response = await apiClient.post<any>('/api/vehicles', payload);
    return mapVehicle(response?.data ?? response);
  },

  async updateVehicle(id: string, payload: Partial<VehiclePayload>): Promise<Vehicle> {
    if (USE_MOCK_API) {
      await delay(600);
      throw new Error('Vehicle update not available in mock mode.');
    }
    const response = await apiClient.put<any>(`/api/vehicles/${id}`, payload);
    return mapVehicle(response?.data ?? response);
  },

  async deleteVehicle(id: string): Promise<void> {
    if (USE_MOCK_API) {
      await delay(500);
      return;
    }
    await apiClient.delete(`/api/vehicles/${id}`);
  },

  // ─── Routes ───────────────────────────────────────────────────────────────

  async getRoutes(params?: { status?: string; page?: number }): Promise<Route[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<ApiCollectionResponse<Route>>(
      '/api/routes',
      params as Record<string, string | number | boolean | undefined>
    );
    return response.data;
  },

  // ─── Passengers & Attendance ──────────────────────────────────────────────

  /**
   * Passengers on a journey — canonical journey_passengers rows mapped to
   * the Passenger display shape. Without an id, uses today's journeys and
   * prefers the in-progress one.
   */
  async getPassengers(journeyId?: string): Promise<Passenger[]> {
    if (USE_MOCK_API) {
      await delay(400);
      return [];
    }
    let journey: any = null;
    if (journeyId) {
      const response = await apiClient.get<any>(`/api/journeys/${journeyId}`);
      journey = response.data ?? response;
    } else {
      const trips = await this.getTodayTrips();
      journey =
        trips.find((t: any) => t.status === 'in_progress') ??
        trips.find((t: any) => t.status === 'scheduled') ??
        trips[0] ??
        null;
    }
    const rows: any[] = journey?.passengers ?? [];
    return rows.map((jp: any) => ({
      id: String(jp.id),
      childId: String(jp.child_id),
      journeyId: String(journey.id),
      name: jp.child ? `${jp.child.first_name} ${jp.child.last_name}` : `Child #${jp.child_id}`,
      child: jp.child ?? null,
      school:
        jp.child?.school?.name ??
        journey?.route?.school?.name ??
        journey?.route?.schools?.[0]?.name ??
        jp.subscription?.transport_service?.school?.name ??
        '',
      pickupPointName: jp.pickup_stop?.name ?? jp.pickupStop?.name ?? '',
      pickupLocation: jp.pickup_stop?.suburb?.name ?? jp.pickup_stop?.name ?? '',
      pickupAddress: jp.child?.pickup_address ?? null,
      stop: jp.pickup_stop?.name ?? jp.pickupStop?.name ?? '',
      stopOrder: 0,
      suburb: jp.pickup_stop?.suburb?.name ?? '',
      status: jp.is_absent
        ? 'absent'
        : jp.dropoff_status === 'dropped_off'
          ? 'dropped_off'
          : jp.pickup_status === 'picked_up'
            ? 'picked_up'
            : 'awaiting',
      pickupTime: jp.picked_up_at ?? null,
      dropoffTime: jp.dropped_off_at ?? null,
      notTravelling: !!jp.is_absent,
      changed: false,
      hasMedical: !!jp.child?.special_requirements,
    }));
  },

  /** Mark a child picked up — canonical journey_passengers pickup.
   * Returns the passenger row (picked_up_at is the authoritative timestamp). */
  async markPickup(journeyId: string, childId: string): Promise<{ picked_up_at?: string | null }> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const res = await apiClient.post<{ data: { picked_up_at?: string | null } }>(
      `/api/driver/trips/${journeyId}/passengers/${childId}/pickup`
    );
    return res.data;
  },

  /** Mark a child dropped off. Returns the passenger row with dropped_off_at. */
  async markDropoff(
    journeyId: string,
    childId: string
  ): Promise<{ dropped_off_at?: string | null }> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const res = await apiClient.post<{ data: { dropped_off_at?: string | null } }>(
      `/api/driver/trips/${journeyId}/passengers/${childId}/dropoff`
    );
    return res.data;
  },

  /** Mark a child absent. */
  async markAbsent(journeyId: string, childId: string, reason?: string): Promise<void> {
    if (USE_MOCK_API) {
      await delay(400);
      return;
    }
    await apiClient.post(`/api/driver/trips/${journeyId}/passengers/${childId}/absent`, {
      reason,
    });
  },

  async getAttendanceRecords(params?: {
    date?: string;
    page?: number;
  }): Promise<AttendanceRecord[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<ApiCollectionResponse<AttendanceRecord>>(
      '/api/attendance',
      params as Record<string, string | number | boolean | undefined>
    );
    return response.data;
  },

  // ─── Active Journey & Trips ────────────────────────────────────────────────

  async getActiveJourney(): Promise<{
    id: string;
    routeId: string;
    routeName: string;
    stops: Array<{
      id: string;
      name: string;
      suburb: string;
      time: string;
      passengersAt: number;
      status: 'completed' | 'current' | 'upcoming';
    }>;
  } | null> {
    if (USE_MOCK_API) {
      await delay(300);
      return null;
    }
    try {
      // Use existing journeys endpoint to find active journey
      const response = await apiClient.get<any>('/api/journeys?status=in_progress');
      const journeys = response.data.data || [];
      if (journeys.length === 0) return null;

      const activeJourney = journeys[0];
      return {
        id: String(activeJourney.id),
        routeId: String(activeJourney.route_id),
        routeName: activeJourney.route?.name || 'Unknown Route',
        stops: activeJourney.stops || [],
      };
    } catch (err) {
      const e = err as { status?: number };
      if (e.status === 404) return null;
      return null; // Return null instead of throwing to prevent 500 errors
    }
  },

  async getJourneys(params?: { date?: string; status?: string }): Promise<any[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<ApiCollectionResponse<any>>(
      '/api/journeys',
      params as Record<string, string | number | boolean | undefined>
    );
    return response.data;
  },

  async startJourney(journeyId: string): Promise<void> {
    if (USE_MOCK_API) {
      await delay(400);
      return;
    }
    await apiClient.post(`/api/journeys/${journeyId}/start`);
  },

  // ─── Driver trips (journeys) — canonical driver endpoints ──────────────────

  /** Today's journeys for the authenticated driver (any status). */
  async getTodayTrips(): Promise<any[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<{ data: any[] }>('/api/driver/trips/today');
    return response.data;
  },

  /**
   * Schedule journey(s) on one of the driver's own routes. A 'both'
   * journey_type creates separate morning + afternoon legs, each with
   * independent passenger check-in — returns a journey or journey array.
   */
  async createTrip(payload: {
    route_id: number | string;
    journey_type: 'morning' | 'afternoon' | 'both';
    journey_date?: string;
    vehicle_id?: number | string;
    transport_service_id?: number | string;
  }): Promise<any | any[]> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const response = await apiClient.post<{ data: any }>('/api/driver/trips', payload);
    return response.data;
  },

  /** Start a scheduled journey — transitions it to in_progress. */
  async startTrip(journeyId: string | number): Promise<any> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const response = await apiClient.post<{ data: any }>(`/api/driver/trips/${journeyId}/start`);
    return response.data;
  },

  /** Complete an in-progress journey. */
  async completeTrip(journeyId: string | number): Promise<any> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const response = await apiClient.post<{ data: any }>(`/api/driver/trips/${journeyId}/complete`);
    return response.data;
  },

  /** Driver arrived at the pickup stop. */
  async markArrived(journeyId: string | number, childId: string | number): Promise<void> {
    await apiClient.post(`/api/driver/trips/${journeyId}/passengers/${childId}/arrived`);
  },

  /** Driver is waiting at the pickup stop — notifies the parent. */
  async markWaiting(journeyId: string | number, childId: string | number): Promise<void> {
    await apiClient.post(`/api/driver/trips/${journeyId}/passengers/${childId}/waiting`);
  },

  /** "I'm running late" — notifies all parents on the journey. */
  async reportDelay(journeyId: string | number, minutes?: number): Promise<void> {
    await apiClient.post(`/api/driver/trips/${journeyId}/delay`, minutes ? { minutes } : {});
  },

  /** Driver availability — emergency matching respects this. */
  async setAvailability(status: 'available' | 'busy' | 'offline' | 'unavailable'): Promise<void> {
    await apiClient.put('/api/driver/availability', { status });
  },

  // ---- Emergency / breakdown ----

  /** Raise a help request on an active journey. Idempotent server-side. */
  async requestEmergency(journeyId: string | number, reason?: string): Promise<any> {
    if (USE_MOCK_API) {
      await delay(400);
      return { id: 1, status: 'open' };
    }
    const response = await apiClient.post<{ data: any; meta: any }>(
      `/api/driver/trips/${journeyId}/emergency`,
      { reason }
    );
    return response.data;
  },

  /** Open emergency requests the driver can help with + their own requests. */
  async getEmergencyRequests(): Promise<{ open: any[]; mine: any[] }> {
    if (USE_MOCK_API) {
      await delay(300);
      return { open: [], mine: [] };
    }
    const response = await apiClient.get<{ data: { open: any[]; mine: any[] } }>(
      '/api/driver/emergency-requests'
    );
    return response.data;
  },

  /** Atomically accept an emergency — passengers transfer to a new journey. */
  async acceptEmergency(requestId: string | number, vehicleId?: number): Promise<any> {
    if (USE_MOCK_API) {
      await delay(400);
      return {};
    }
    const response = await apiClient.post<{ data: any }>(
      `/api/driver/emergency-requests/${requestId}/accept`,
      vehicleId ? { vehicle_id: vehicleId } : {}
    );
    return response.data;
  },

  async cancelEmergency(requestId: string | number): Promise<void> {
    if (USE_MOCK_API) {
      await delay(300);
      return;
    }
    await apiClient.post(`/api/driver/emergency-requests/${requestId}/cancel`);
  },

  /** Driver's own routes (for journey scheduling). */
  async getMyRoutes(): Promise<any[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response = await apiClient.get<any>('/api/driver/routes');
    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : (response?.data?.data ?? []);
    return list;
  },

  async completeJourney(journeyId: string): Promise<void> {
    if (USE_MOCK_API) {
      await delay(400);
      return;
    }
    await apiClient.post(`/api/journeys/${journeyId}/complete`);
  },

  /** Report an incident on the active journey — creates a real SafetyIncident. */
  async reportIncident(
    journeyId: string,
    payload: { type: string; description: string; child_id?: string; severity?: string }
  ): Promise<void> {
    await apiClient.post(`/api/driver/trips/${journeyId}/incidents`, payload);
  },

  // ─── Earnings & Withdrawals ────────────────────────────────────────────────

  async getWithdrawals(): Promise<WithdrawalRecord[]> {
    if (USE_MOCK_API) {
      await delay(300);
      return [];
    }
    const response =
      await apiClient.get<ApiCollectionResponse<WithdrawalRecord>>('/api/driver/withdrawals');
    return response.data;
  },

  async requestWithdrawal(payload: WithdrawalRequestPayload): Promise<void> {
    if (USE_MOCK_API) {
      await delay(500);
      return;
    }
    await apiClient.post('/api/driver/withdrawals', payload);
  },

  /** Authoritative payout balances (available/pending/reserved/next payout). */
  async getBalances(): Promise<DriverBalances> {
    const response = await apiClient.get<ApiSingleResponse<DriverBalances>>(
      '/api/driver/balance'
    );
    return response.data;
  },

  /** Payout history — canonical payout records. */
  async getPayouts(): Promise<WithdrawalRecord[]> {
    const response =
      await apiClient.get<ApiCollectionResponse<WithdrawalRecord>>('/api/driver/payouts');
    const d: any = response.data as any;
    return Array.isArray(d) ? d : (d?.data ?? []);
  },

  // ─── KPI Stats ────────────────────────────────────────────────────────────

  async getDashboardStats(): Promise<{
    totalPassengers: number;
    pickedUp: number;
    absent: number;
    capacity: number;
    activePassengers: number;
    unresolvedCount: number;
  }> {
    if (USE_MOCK_API) {
      await delay(300);
      return {
        totalPassengers: 0,
        pickedUp: 0,
        absent: 0,
        capacity: 0,
        activePassengers: 0,
        unresolvedCount: 0,
      };
    }
    // Calculate stats from journeys since endpoint doesn't exist in Laravel
    try {
      const journeys = await this.getJourneys();
      const totalPassengers = journeys.reduce((sum, j) => sum + (j.passengers?.length || 0), 0);
      const pickedUp = journeys.reduce(
        (sum, j) => sum + (j.passengers?.filter((p: any) => p.status === 'picked_up').length || 0),
        0
      );
      const absent = journeys.reduce(
        (sum, j) => sum + (j.passengers?.filter((p: any) => p.status === 'absent').length || 0),
        0
      );

      return {
        totalPassengers,
        pickedUp,
        absent,
        capacity: totalPassengers,
        activePassengers: totalPassengers - pickedUp - absent,
        unresolvedCount: 0,
      };
    } catch {
      return {
        totalPassengers: 0,
        pickedUp: 0,
        absent: 0,
        capacity: 0,
        activePassengers: 0,
        unresolvedCount: 0,
      };
    }
  },

  // ─── Incidents ────────────────────────────────────────────────────────────

  async getIncidents(): Promise<Incident[]> {
    if (USE_MOCK_API) {
      await delay(400);
      return [];
    }
    const response = await apiClient.get<ApiCollectionResponse<Incident>>('/api/incidents');
    return response.data;
  },

  async createIncident(payload: IncidentCreatePayload): Promise<Incident> {
    if (USE_MOCK_API) {
      await delay(800);
      throw new Error('Incident reporting not available in mock mode.');
    }
    const response = await apiClient.post<ApiSingleResponse<Incident>>('/api/incidents', {
      type: payload.type,
      severity: payload.severity,
      route_id: payload.routeId,
      children_affected: payload.childrenAffected,
      description: payload.description,
      notes: payload.notes,
    });
    return response.data;
  },

  async updateIncident(
    id: string,
    payload: Partial<{ status: string; notes: string; resolved_at: string }>
  ): Promise<Incident> {
    if (USE_MOCK_API) {
      await delay(500);
      throw new Error('Incident update not available in mock mode.');
    }
    const response = await apiClient.put<ApiSingleResponse<Incident>>(
      `/api/incidents/${id}`,
      payload
    );
    return response.data;
  },

  // ─── Pre-Route Check ────────────────────────────────────────────────────────

  async preRouteCheck(routeId?: string): Promise<{
    vehicle_valid: boolean;
    documents_valid: boolean;
    driver_verified: boolean;
    vehicle: any;
    documents: any[];
  }> {
    if (USE_MOCK_API) {
      await delay(400);
      return {
        vehicle_valid: true,
        documents_valid: true,
        driver_verified: true,
        vehicle: null,
        documents: [],
      };
    }
    const response = await apiClient.get<
      ApiSingleResponse<{
        vehicle_valid: boolean;
        documents_valid: boolean;
        driver_verified: boolean;
        vehicle: any;
        documents: any[];
      }>
    >('/api/driver/pre-route-check');
    return response.data;
  },

  // ─── Earnings ───────────────────────────────────────────────────────────────

  async getEarnings(params?: { start_date?: string; end_date?: string }): Promise<{
    total_earnings: number;
    completed_journeys: number;
    total_passengers: number;
    average_rating: number;
    breakdown: Array<{
      date: string;
      amount: number;
      journey_count: number;
    }>;
  }> {
    if (USE_MOCK_API) {
      await delay(400);
      return {
        total_earnings: 0,
        completed_journeys: 0,
        total_passengers: 0,
        average_rating: 0,
        breakdown: [],
      };
    }
    const response = await apiClient.get<
      ApiSingleResponse<{
        total_earnings: number;
        completed_journeys: number;
        total_passengers: number;
        average_rating: number;
        breakdown: Array<{
          date: string;
          amount: number;
          journey_count: number;
        }>;
      }>
    >('/api/driver/earnings', params as Record<string, string | number | boolean | undefined>);
    return response.data;
  },

  // ─── Settings ───────────────────────────────────────────────────────────────

  async getSettings(): Promise<{
    notifications_enabled: boolean;
    location_sharing_enabled: boolean;
    preferred_theme: string;
    working_hours: any;
    emergency_contact: {
      name: string;
      phone: string;
    };
  }> {
    if (USE_MOCK_API) {
      await delay(300);
      return {
        notifications_enabled: true,
        location_sharing_enabled: true,
        preferred_theme: 'light',
        working_hours: null,
        emergency_contact: {
          name: '',
          phone: '',
        },
      };
    }
    const response = await apiClient.get<
      ApiSingleResponse<{
        notifications_enabled: boolean;
        location_sharing_enabled: boolean;
        preferred_theme: string;
        working_hours: any;
        emergency_contact: {
          name: string;
          phone: string;
        };
      }>
    >('/api/driver/settings');
    return response.data;
  },

  async updateSettings(payload: {
    notifications_enabled?: boolean;
    location_sharing_enabled?: boolean;
    preferred_theme?: string;
    working_hours?: any;
    emergency_contact?: {
      name: string;
      phone: string;
    };
  }): Promise<void> {
    if (USE_MOCK_API) {
      await delay(400);
      return;
    }
    await apiClient.put('/api/driver/settings', payload);
  },
};
